import {
  Box,
  Button,
  FormControl,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Logo from "../../assets/copysa.svg";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import Spinner from "../../components/Spinner";
import toast from "react-hot-toast";
import PhoneEnabledIcon from "@mui/icons-material/PhoneEnabled";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

const ForgetPass = () => {
  const [phone, setphone] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate(); // Initialize useNavigate
  const [language, setLanguage] = useState(i18n.language);
  const isRtl = i18n.dir() === "rtl";
  const [anchorEl, setAnchorEl] = useState(null);
  const languageOptions = [
    { value: "en", label: "English" },
    { value: "fa", label: "فارسی" },
  ];
  useEffect(() => {
    const onLanguageChanged = (lng) => setLanguage(lng);
    i18n.on("languageChanged", onLanguageChanged);
    return () => {
      i18n.off("languageChanged", onLanguageChanged);
    };
  }, []);
  const handleLangSelect = (lang) => {
    i18n.changeLanguage(lang);
    document.cookie = `i18next=${lang}; path=/; domain=.copysa.ir; max-age=31536000`;

    setAnchorEl(null);
  };
  const phoneRegex = /^(09|(\+98))\d{9}$/;
  const handleForgetPass = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setLoading(true);

    try {
      const response = await fetch(
        `https://api.copysa.ir/api/User/ForgetPassword/${phone}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );

      // Check if the response is successful (HTTP status 200)
      if (response.ok) {
        toast.success(t("Toast.newpass"));
        navigate("/auth/login", { state: { phone } });
      } else {
        toast.error(t("Toast.servererror"));
      }
    } catch (err) {
      toast.error(err.response?.data || t("Toast.passerror"), {
        duration: 5000,
      });
    } finally {
      setLoading(false); // Ensure loading is turned off after API call
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "#F7F2FB",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header Section */}
      <Box
        component="header"
        sx={{
          backgroundColor: "#512c63",
          width: "100%",
          paddingX: 2,
          paddingY: 3,
          mb: 2,
          position: "sticky",
          top: 0,
          minHeight: "250px",
          zIndex: 10,
        }}
      >
        {/* Content container */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexDirection: "column",
            alignItems: "center",
            maxWidth: "1200px",
            margin: "0 auto",
            height: "100%",
          }}
        >
          {/* Left: Language Selector */}
          <Box
            sx={{
              width: "100%",
              mb: 5,
              display: "flex",
              justifyContent: "flex-start",
            }}
            dir="rtl"
          >
            <FormControl variant="outlined" size="small">
              <Select
                value={language}
                onChange={(e) => handleLangSelect(e.target.value)}
                sx={{
                  color: "white",
                  border: "1px solid white",
                  borderRadius: 2,
                  px: 2,
                  backgroundColor: "rgba(255,255,255,0.08)",
                  "& .MuiSvgIcon-root": { color: "white" },
                }}
                disableUnderline
                aria-label={t("aria.selectLanguage")}
              >
                {languageOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    aria-label={t("aria.selectLanguage", {
                      lang: option.label,
                    })}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography fontSize={20}>{option.icon}</Typography>
                      <Typography>{option.label}</Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {/* Center: Logo */}
          <Box sx={{ textAlign: "center" }}>
            <img src={Logo} width={225} height={100} alt="copysa logo" />
          </Box>
          {/* Right: Placeholder for future elements */}
        </Box>

        {/* Wave Divider */}
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            lineHeight: 0,
            zIndex: -2,
          }}
        >
          <svg
            data-name="Layer 1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
            style={{
              display: "block",
              width: "100%",
              height: "50px",
              transform: "rotateY(180deg)",
              marginBottom: "-5px",
            }}
          >
            <path
              d="M985.66,92.83C906.67,72,823.78,31,743.84,14.19c-82.26-17.34-168.06-16.33-250.45.39-57.84,11.73-114,31.07-172,41.86A600.21,600.21,0,0,1,0,27.35V120H1200V95.8C1132.19,118.92,1055.71,111.31,985.66,92.83Z"
              fill="#F7F2FB"
            />
          </svg>
        </Box>
      </Box>

      {/* Main Content Section */}
      <Box
        component="main"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: "2",
        }}
      >
        <Box
          component="form"
          onSubmit={handleForgetPass}
          sx={{
            color: "black",
            width: { xs: "100%", md: "30%" },
            display: "flex",
            flexDirection: "column",
            paddingX: "2rem",
          }}
        >
          <Typography
            textAlign="center"
            variant="h1"
            marginBottom={5}
            fontSize={26}
          >
            {t("Auth.Forget")}
          </Typography>

          {/* Phone Number Field */}
          <TextField
            required
            name="number"
            value={phone}
            helperText={error}
            error={!!error}
            onChange={(e) => setphone(e.target.value)}
            id="number"
            label={t("Auth.Phone")}
            variant="outlined"
            fullWidth
            sx={{ marginBottom: "1.75rem" }}
            InputLabelProps={{
              required: false,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneEnabledIcon />
                </InputAdornment>
              ),
            }}
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
              autoComplete: "tel",
            }}
          />
        </Box>
      </Box>

      {/* Footer Section */}
      <Box
        component="footer"
        sx={{
          pb: "2rem",
          textAlign: "center",
          width: { xs: "86%", md: "30%" },
          marginX: "auto",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {/* Submit Button */}
        <Button
          type="submit"
          onClick={handleForgetPass}
          disabled={loading || !phoneRegex.test(phone)} // Disable if loading or phone format is invalid
          variant="contained"
          color="primary"
          sx={{
            width: "100%",
            borderRadius: "8px",
            my: "1rem",
            padding: "12px",
            fontSize: "16px",
          }}
          size="large"
        >
          {t("Auth.Receive")}
          {loading && <Spinner />}
        </Button>
        <Typography variant="h5" component="h1">
          {t("Auth.Noacc")}
          <Link to="/auth/signup" className="text-blue mx-1">
            {t("Auth.SignUp")}
          </Link>
        </Typography>

        <Link to="/auth/login" className="text-blue m-4">
          <Typography>{t("Auth.SignIn")}</Typography>
        </Link>
      </Box>
    </Box>
  );
};

export default ForgetPass;
